import { useExportApi } from '../UseExportApi';
import { ExportChartOptions } from '../Model';
import { useCallback } from 'react';
import { exportTable } from './ExportTable';
import { FactRevenue, Filters, Option, PageName } from '@utils/pageConfig/Types';
import { PerformanceAnalysisboardPg } from '@models/PerformanceAnalysisboard';
import { CategoryLeaderboardPg } from '@models/CategoryLeaderboard';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { OpportunityReviewTableContent } from '@models/OpportunityReview';
import { ExportTrackingPageView } from '../export-tracking/ExportTracking';
import { KpiDataDto } from '@data-visualization/performance-analysis-line-chart/Types';
import { LeaderboardKpi, ProductsCompareExport } from '@models/Products';
import { useGetLoadedProductGroups } from '../UseGetLoadedProductGroups';
import { useGetLoadedCountries } from '../UseGetLoadedCountries';
import { CapturedImage } from '@ecosystem/newron-design-system/build/export-utils/captureImage';
import { BrandDeepDiveChartRespDataLookup, BrandDependencyChartSubResp } from '@models/BrandDependency';

export interface ExportTableOptions extends ExportChartOptions {
  title: string;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  data:
    | PerformanceAnalysisboardPg[]
    | CategoryLeaderboardPg[]
    | OpportunityReviewTableContent[]
    | KpiDataDto[]
    | LeaderboardKpi[]
    | ProductsCompareExport[]
    | BrandDependencyChartSubResp[]
    | Map<string, BrandDeepDiveChartRespDataLookup>;
  exportImage?: CapturedImage | CapturedImage[];
  summaryData?: CategoryLeaderboardPg[] | LeaderboardKpi[] | OpportunityReviewTableContent[];
  timePeriod: string;
  comparisonTimePeriod: string;
  factRevenue: FactRevenue;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  selectedKpi: Option;
  pageName: PageName;
  pagesConfig?: PagesConfig;
  pageView?: ExportTrackingPageView;
  volume?: string;
  sortBy?: string;
  sortDirection?: string;
  sortKey?: string;
  retailClientName?: string;
  isDeepDiveView?: boolean;
  segmentBy?: string;
  sortedDeepDiveBrands?: string[];
}

export function useExportTable(prodLeaderboardFeatureProductFilters: Filters[]): {
  download: (options: ExportTableOptions) => Promise<void>;
} {
  const { exportFile: exportFn } = useExportApi();
  const { loadedProductGroups } = useGetLoadedProductGroups();
  const loadedCountries = useGetLoadedCountries();

  const download = useCallback(
    async (options: ExportTableOptions) => {
      await exportTable(
        {
          exportFn,
          ...options,
          ...loadedProductGroups,
          ...loadedCountries,
        },
        prodLeaderboardFeatureProductFilters,
      );
    },
    [loadedProductGroups],
  );

  return {
    download,
  };
}
